import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

type HtmlAttributes = {
  lang?: string;
};

type HelmetMeta = {
  charSet?: string;
  content?: string;
  httpEquiv?: string;
  name?: string;
  property?: string;
};

type TitleStore = { current: string | null };

type DocumentTitleProviderProps = {
  children: React.ReactNode;
  defaultTitle?: string;
  htmlAttributes?: HtmlAttributes;
  meta?: HelmetMeta[];
  titleTemplate?: string;
};

export type HelmetProps = {
  defaultTitle?: string;
  htmlAttributes?: HtmlAttributes;
  meta?: HelmetMeta[];
  title?: string;
  titleTemplate?: string;
};

const TitleStoreContext = createContext<TitleStore | null>(null);
const TitleNotifyContext = createContext<(() => void) | null>(null);

const applyTemplate = (title: string, template?: string) => {
  if (!template) {
    return title;
  }

  return template.replace(/%s/g, () => title);
};

const isPageTitleHelmet = (props: HelmetProps) =>
  props.title != null &&
  props.titleTemplate == null &&
  props.defaultTitle == null &&
  props.htmlAttributes == null &&
  props.meta == null;

const DocumentMetadata = ({
  defaultTitle,
  meta,
  store,
  titleTemplate,
}: {
  defaultTitle?: string;
  meta?: HelmetMeta[];
  store: TitleStore;
  titleTemplate?: string;
}) => {
  const title = store.current
    ? applyTemplate(store.current, titleTemplate)
    : defaultTitle;

  return (
    <>
      {title ? <title>{title}</title> : null}
      {meta?.map((item) => (
        <meta
          key={item.name ?? item.property ?? item.httpEquiv ?? item.charSet}
          charSet={item.charSet}
          content={item.content}
          httpEquiv={item.httpEquiv}
          name={item.name}
          property={item.property}
        />
      ))}
    </>
  );
};

export function DocumentTitleProvider({
  children,
  defaultTitle,
  htmlAttributes,
  meta,
  titleTemplate,
}: DocumentTitleProviderProps) {
  const location = useLocation();
  const storeRef = useRef<TitleStore>({ current: null });
  const locationKeyRef = useRef(location.key);
  const [, setVersion] = useState(0);

  if (locationKeyRef.current !== location.key) {
    locationKeyRef.current = location.key;
    storeRef.current.current = null;
  }

  const notify = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useLayoutEffect(() => {
    if (!htmlAttributes?.lang) {
      return undefined;
    }

    document.documentElement.lang = htmlAttributes.lang;
    return undefined;
  }, [htmlAttributes?.lang]);

  return (
    <TitleStoreContext.Provider value={storeRef.current}>
      <TitleNotifyContext.Provider value={notify}>
        {children}
        <DocumentMetadata
          defaultTitle={defaultTitle}
          meta={meta}
          store={storeRef.current}
          titleTemplate={titleTemplate}
        />
      </TitleNotifyContext.Provider>
    </TitleStoreContext.Provider>
  );
}

export function Helmet(props: HelmetProps) {
  const store = useContext(TitleStoreContext);
  const notify = useContext(TitleNotifyContext);
  const isPageTitle = isPageTitleHelmet(props);
  const title =
    store && isPageTitle && props.title != null ? String(props.title) : null;

  if (store && title) {
    store.current = title;
  }

  useLayoutEffect(() => {
    if (!store || !title || !notify) {
      return undefined;
    }

    store.current = title;
    notify();

    return () => {
      if (store.current === title) {
        store.current = null;
        notify();
      }
    };
  }, [notify, store, title]);

  if (title) {
    return null;
  }

  const resolvedTitle = props.title
    ? applyTemplate(props.title, props.titleTemplate)
    : props.defaultTitle;

  return (
    <>
      {resolvedTitle ? <title>{resolvedTitle}</title> : null}
      {props.meta?.map((item) => (
        <meta
          key={item.name ?? item.property ?? item.httpEquiv ?? item.charSet}
          charSet={item.charSet}
          content={item.content}
          httpEquiv={item.httpEquiv}
          name={item.name}
          property={item.property}
        />
      ))}
    </>
  );
}
